import { useConnector } from "react-instantsearch-hooks-web";
import connectRange from "instantsearch.js/es/connectors/range/connectRange";
import noUiSlider from "nouislider";

import type {
  RangeConnectorParams,
  RangeWidgetDescription,
  RangeBoundaries,
} from "instantsearch.js/es/connectors/range/connectRange";
import { useEffect, useRef } from "react";
import type { API } from "nouislider";
import React from "react";
import { Box } from "theme-ui";
import { useKioskContext } from "./KioskContext";

export type UseRangeSliderProps = RangeConnectorParams;

export function useRangeSlider(props?: UseRangeSliderProps) {
  return useConnector<RangeConnectorParams, RangeWidgetDescription>(
    connectRange,
    props
  );
}

type RangeSliderProps = {
  step: number;
  unit?: string;
  label?: string;
};

export function RangeSlider({
  attribute,
  step,
  unit,
  label,
}: UseRangeSliderProps & RangeSliderProps) {
  // Reference to the DOM element for the noUiSlider library
  const noUiSliderRef = useRef(null);
  // Reference to the noUiSlider instance
  const sliderRef = useRef<API | undefined>(undefined);

  // Use the useRangeSlider hook to get the range and refine function
  const { range, refine, start } = useRangeSlider({
    attribute,
    precision: step,
  });

  const [startValue, endValue] = start;

  // Get references to global context elements (if they exist)
  const { abvRangeElement, sizeRangeElement, yearRangeElement } =
    useKioskContext();

  useEffect(() => {
    const noUiSliderElement = noUiSliderRef.current;

    if (!sliderRef.current && noUiSliderElement) {
      const minValue = range.min || 0;
      const maxValue = range.max || 0;

      // Create the noUiSlider instance
      sliderRef.current = noUiSlider.create(noUiSliderElement, {
        animate: false,
        start: [startValue || minValue, endValue || maxValue],
        range: { min: minValue, max: maxValue },
        connect: true,
        step,
        format: {
          to: (value) => (unit ? value.toFixed(1) : value),
          from: (value) => Number(value),
        },
        tooltips: {
          to: (value) =>
            `${unit ? value.toFixed(1) : value}${unit ? unit : ""}`,
        },
      });

      // Save the noUiSlider instance to the appropriate global context element
      if (attribute === "abv") {
        abvRangeElement.current = sliderRef.current;
      }
      if (attribute === "sizegroup") {
        sizeRangeElement.current = sliderRef.current;
      }
      if (attribute === "year") {
        yearRangeElement.current = sliderRef.current;
      }

      // Callback function for when the user changes the slider values
      const onInput = (values: (number | string)[]) => {
        // Check if the input array has at least two values
        if (values.length < 2) {
          return;
        }

        // Convert the input values to numbers using parseFloat
        const [firstValue, secondValue] = values.map((value) =>
          typeof value === "string" ? parseFloat(value) : value
        );

        // Calculate the refined minimum and maximum values for the range
        let computedMin = Math.min(Math.max(firstValue, minValue), maxValue);
        let computedMax = Math.max(Math.min(secondValue, maxValue), minValue);

        // Adjust the computed values if they are equal
        if (computedMin === computedMax) {
          // If the computed value is greater than the minimum, decrement the minimum
          if (computedMin > minValue) {
            computedMin -= 1;
          }
          // If the computed value is less than the maximum, increment the maximum
          else if (computedMax < maxValue) {
            computedMax += 1;
          }
        }

        // Create a new array with the refined minimum and maximum values
        const refineValues: RangeBoundaries = [computedMin, computedMax];

        // Call the refine function with the refined values
        refine(refineValues);
      };
      sliderRef.current.on("start", (_empty, handleNumber) => {
        const handles = document.querySelectorAll<HTMLDivElement>(
          `.rangeSlider-${attribute} .noUi-origin`
        );

        const activeHandle = handles[handleNumber];
        if (activeHandle) {
          activeHandle.style.zIndex = "10";
        }
      });

      sliderRef.current.on("change", onInput);
    }
  }, [
    step,
    attribute,
    unit,
    range,
    refine,
    abvRangeElement,
    sizeRangeElement,
    yearRangeElement,
  ]);

  useEffect(() => {
    if (sliderRef.current) {
      const minValue = range.min || 0;
      const maxValue = range.max || 0;
      sliderRef.current.updateOptions(
        {
          start: [minValue, maxValue],
          range: { min: minValue, max: maxValue },
          step,
        },
        true
      );
    }
  }, [range.max, range.min]);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", width: "full" }}
      className={`rangeSlider-${attribute}`}
    >
      <div ref={noUiSliderRef}></div>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mx: "-1.25rem",
          mt: "2.5rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
          }}
        >
          <Box as={"span"}>
            {unit ? range.min?.toFixed(1) : range.min} {unit}
          </Box>
          {label}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
          }}
        >
          <Box as={"span"}>
            {unit ? range.max?.toFixed(1) : range.max} {unit}
          </Box>
          {label}
        </Box>
      </Box>
    </Box>
  );
}
